import "@/App.css";
import "@/styles/theme.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/custom/AppShell";
import DesktopLogin from "@/pages/DesktopLogin";
import Dashboard from "@/pages/Dashboard";
import NewJob from "@/pages/NewJob";
import MaterialsCatalogue from "@/pages/MaterialsCatalogue";
import QuoteForm from "@/pages/QuoteForm";
import QuoteView from "@/pages/QuoteView";
import CalendarPage from "@/pages/CalendarPage";
import Financials from "@/pages/Financials";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Invoices from "@/pages/Invoices";
import PaymentMatching from "@/pages/PaymentMatching";
import Quotes from "@/pages/Quotes";
import Todos from "@/pages/Todos";
import Settings from "@/pages/Settings";
import JobSheet from "@/pages/JobSheet";
import JobProfitCalculator from "@/pages/JobProfitCalculator";
import MobileSelect from "@/pages/mobile/MobileSelect";
import MobileToday from "@/pages/mobile/MobileToday";
import MobileJob from "@/pages/mobile/MobileJob";
import MobileQuoteEdit from "@/pages/mobile/MobileQuoteEdit";
import VersionDebug from "@/pages/VersionDebug";
import UpdateGate from "@/components/custom/UpdateGate";

const SITE_PASSWORD = null; // Verified server-side

function SiteGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem("site_unlocked") === "true");
  const [input, setInput] = useState("");
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(false);

  if (unlocked) return children;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-site`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password: input })
      }).then(r => { if (!r.ok) throw new Error(); return r.json(); });
      localStorage.setItem("site_unlocked", "true");
      setUnlocked(true);
    } catch { setError(true); setInput(""); }
    finally { setChecking(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#0B1929" }}>
      <div className="w-full max-w-xs">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          </div>
          <h1 className="text-white text-lg font-semibold">HPS Team Portal</h1>
          <p className="text-slate-400 text-sm mt-1">Enter the access code to continue</p>
        </div>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={input}
            onChange={(e) => { setInput(e.target.value); setError(false); }}
            placeholder="Access code"
            autoFocus
            className="w-full h-12 rounded-xl px-4 text-center text-lg tracking-widest focus:outline-none"
            style={{ background: "rgba(255,255,255,0.06)", border: error ? "2px solid #EF4444" : "1px solid rgba(255,255,255,0.1)", color: "#fff" }}
            data-testid="site-password-input"
          />
          {error && <p className="text-red-400 text-xs text-center mt-2">Incorrect code</p>}
          <button type="submit" className="w-full h-11 rounded-xl mt-4 text-sm font-semibold transition-colors" style={{ background: "#1E40AF", color: "#fff" }} data-testid="site-password-submit">
            Enter
          </button>
        </form>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const userId = localStorage.getItem("desktop_user_id");
  if (!userId) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

function App() {
  // Apply saved theme on mount
  useEffect(() => {
    const saved = localStorage.getItem("app_theme") || "warm";
    document.documentElement.setAttribute("data-theme", saved);
  }, []);

  // Register the service worker (cache cleanup + skipWaiting happen inside sw.js).
  // `updateViaCache: 'none'` tells the browser NEVER to serve the sw.js script itself from the
  // HTTP cache when checking for updates — critical on production where the CDN may cache
  // /sw.js for up to 5 minutes. Without this, the browser could think the SW is up-to-date when
  // a newer one is actually deployed. See PRODUCTION_CACHE_HEADERS.md.
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).catch(() => { /* non-blocking */ });
    }
  }, []);

  return (
    <UpdateGate>
      <SiteGate>
        <BrowserRouter>
          <Toaster position="top-center" richColors toastOptions={{ style: { fontSize: "13px", padding: "8px 16px", maxWidth: "320px" } }} />
        <Routes>
          <Route path="/login" element={<DesktopLogin />} />
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/jobs/new" element={<ProtectedRoute><NewJob /></ProtectedRoute>} />
          <Route path="/materials" element={<ProtectedRoute><MaterialsCatalogue /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
          <Route path="/financials" element={<ProtectedRoute><Financials /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
          <Route path="/clients/:clientId" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
          <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
          <Route path="/payments" element={<ProtectedRoute><PaymentMatching /></ProtectedRoute>} />
          <Route path="/quotes-list" element={<ProtectedRoute><Quotes /></ProtectedRoute>} />
          <Route path="/todos" element={<ProtectedRoute><Todos /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/quotes/new" element={<ProtectedRoute><QuoteForm /></ProtectedRoute>} />
          <Route path="/quotes/:id/edit" element={<ProtectedRoute><QuoteForm /></ProtectedRoute>} />
          <Route path="/quotes/:id/profit" element={<ProtectedRoute><JobProfitCalculator /></ProtectedRoute>} />
          <Route path="/quotes/:id" element={<ProtectedRoute><QuoteView /></ProtectedRoute>} />
          <Route path="/jobs/:id" element={<ProtectedRoute><JobSheet /></ProtectedRoute>} />
          <Route path="/mobile" element={<MobileSelect />} />
          <Route path="/mobile/login" element={<Navigate to="/mobile" replace />} />
          <Route path="/team" element={<MobileSelect />} />
          <Route path="/mobile/today" element={<MobileToday />} />
          <Route path="/mobile/job/:id" element={<MobileJob />} />
          <Route path="/mobile/quote/:id" element={<MobileQuoteEdit />} />
          {/* Diagnostic page for forced-version-update triage. Behind the site-access gate only —
              contractors can be sent the URL directly when they're stuck on an old build. */}
          <Route path="/admin/version-debug" element={<VersionDebug />} />
          {/* Catch-all — send unknown paths to dashboard (or login for unauth users via ProtectedRoute) */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </SiteGate>
    </UpdateGate>
  );
}

export default App;
